/**
 * Static Data Context
 * 
 * Manages rarely-changing reference data (providers, locations, CPT codes, etc.)
 * This data is loaded once on login and shared across the app.
 */

import React, { createContext, useContext, useState, useMemo, useRef, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const StaticDataContext = createContext();

export const StaticDataProvider = ({ children }) => {
  // Loaded once on login - rarely changes
  const [providers, setProviders] = useState([]);
  const [locations, setLocations] = useState([]);
  const [posCodes, setPosCodes] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  
  // Admin/Super Admin data
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  
  // Billing reference data
  const [cptCodes, setCptCodes] = useState([]);
  const [cptBundles, setCptBundles] = useState([]);
  
  // Form templates
  const [formTemplates, setFormTemplates] = useState([]);
  const [formTypes, setFormTypes] = useState([]);
  
  // Treatment planning
  const [serviceTypes, setServiceTypes] = useState([]);
  const [intakeBundles, setIntakeBundles] = useState([]);
  
  // Groups
  const [allGroups, setAllGroups] = useState([]);
  
  // Branding
  const [brandingSettings, setBrandingSettings] = useState(null);
  
  // Track if static data has been loaded
  const [staticDataLoaded, setStaticDataLoaded] = useState(false);

  // Jun 2026 — self-contained lazy loader for form templates/types.
  // The legacy App.js shell lazy-loads these only on its own tabs
  // (medical-records/admin/chart), so V2 surfaces that need templates
  // (e.g. the Create Group Notes dialog) found an empty list. Any V2
  // consumer can call this on open; it no-ops once loaded.
  const formsLoadedRef = useRef(false);
  const ensureFormTemplatesLoaded = useCallback(async () => {
    if (formsLoadedRef.current) return;
    formsLoadedRef.current = true;
    try {
      const [templatesRes, typesRes] = await Promise.all([
        axios.get(`${API}/api/form-templates`),
        axios.get(`${API}/api/form-types`),
      ]);
      setFormTemplates((prev) => (prev && prev.length ? prev : (templatesRes.data || [])));
      setFormTypes((prev) => (prev && prev.length ? prev : (typesRes.data || [])));
    } catch (e) {
      formsLoadedRef.current = false; // retry on next open
    }
  }, []);

  // Phase-1 stabilization (Feb 2026): memoize the Provider value so
  // consumers don't re-render every time StaticDataProvider re-renders.
  // This context holds rarely-changing reference data (providers, CPT
  // codes, locations, etc.) loaded once on login — so a stable identity
  // is especially valuable here.  Setters from ``useState`` are stable
  // and intentionally NOT in the deps array.
  const value = useMemo(() => ({
    // Core reference data
    providers,
    setProviders,
    locations,
    setLocations,
    posCodes,
    setPosCodes,
    availableStatuses,
    setAvailableStatuses,
    
    // Users & roles
    users,
    setUsers,
    roles,
    setRoles,
    organizations,
    setOrganizations,
    
    // Billing
    cptCodes,
    setCptCodes,
    cptBundles,
    setCptBundles,
    
    // Forms
    formTemplates,
    setFormTemplates,
    formTypes,
    setFormTypes,
    
    // Treatment planning
    serviceTypes,
    setServiceTypes,
    intakeBundles,
    setIntakeBundles,
    
    // Groups
    allGroups,
    setAllGroups,
    
    // Branding
    brandingSettings,
    setBrandingSettings,
    
    // Loading state
    staticDataLoaded,
    setStaticDataLoaded,
    ensureFormTemplatesLoaded,
  }), [
    providers, locations, posCodes, availableStatuses,
    users, roles, organizations,
    cptCodes, cptBundles,
    formTemplates, formTypes,
    serviceTypes, intakeBundles,
    allGroups,
    brandingSettings,
    staticDataLoaded,
    ensureFormTemplatesLoaded,
  ]);

  return (
    <StaticDataContext.Provider value={value}>
      {children}
    </StaticDataContext.Provider>
  );
};

export const useStaticData = () => {
  const context = useContext(StaticDataContext);
  if (!context) {
    throw new Error('useStaticData must be used within StaticDataProvider');
  }
  return context;
};

export default StaticDataContext;
